import React, { forwardRef } from 'react';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { Icon } from '../../../components/base/Icon';
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons';
import { GridField } from '@supply-mover-2-0/core';

interface GridLayoutItemProps {
  field: GridField;
  children: React.ReactNode;
  editMode?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
  style?: React.CSSProperties;
  className?: string;
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseUp?: (e: React.MouseEvent) => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
}

export const GridLayoutItem = forwardRef<HTMLDivElement, GridLayoutItemProps & { 'data-grid'?: any }>(({
  field,
  children,
  editMode = false,
  isSelected = false,
  onSelect,
  style,
  className,
  onMouseDown,
  onMouseUp,
  onTouchEnd,
  'data-grid': dataGrid,
  ...props
}, ref) => {
  const colors = useThemeColors();

  // Combine styles
  const combinedStyle: React.CSSProperties = {
    ...style,
    position: 'relative',
    width: '100%',
    height: '100%',
    backgroundColor: colors.values.bgPrimary,
    borderRadius: 4,
    overflow: 'hidden',
    ...(editMode && {
      border: `1px solid ${isSelected ? colors.values.primary : colors.values.borderDefault}`,
      cursor: 'move'
    })
  };

  const content = (
    <>
      {/* Drag handle for edit mode */}
      {editMode && (
        <div 
          className="grid-drag-handle"
          style={{
            position: 'absolute',
            top: 4,
            left: 4,
            padding: 6,
            backgroundColor: colors.values.bgPrimary,
            borderRadius: 4,
            cursor: 'grab',
            zIndex: 10,
            border: `1px solid ${colors.values.borderDefault}`,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Icon icon={faGripVertical} size="sm" color={colors.values.primary} />
        </div>
      )}
      
      {/* Content container */}
      <div style={{ 
        padding: 8, 
        width: '100%', 
        height: '100%',
        maxHeight: '100%',
        overflow: 'hidden', // Ensure content fits within allocated height
        display: 'flex',
        flexDirection: 'column'
      }}>
        {children}
      </div>

      {/* Resize handle visual indicator (actual handle is added by react-grid-layout) */}
      {editMode && field.gridStyle?.isResizable !== false && (
        <div style={{
          position: 'absolute',
          bottom: 2,
          right: 2,
          width: 16,
          height: 16,
          pointerEvents: 'none'
        }}>
          <div style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: 0,
            height: 0,
            borderStyle: 'solid',
            borderLeft: `16px solid transparent`,
            borderTop: `16px solid ${colors.values.primary}`,
            opacity: 0.5
          }} />
        </div>
      )}
    </>
  );

  if (editMode && onSelect) {
    return (
      <div
        ref={ref}
        style={combinedStyle}
        className={className}
        onClick={onSelect}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onTouchEnd={onTouchEnd}
        data-grid={dataGrid}
        {...props}
      >
        {content}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      style={combinedStyle}
      className={className}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onTouchEnd={onTouchEnd}
      data-grid={dataGrid}
      {...props}
    >
      {content}
    </div>
  );
});